<script setup>
/**
 * The shell: header, the routed view, toasts — and the three states the whole
 * application can be in before any of that is meaningful (booting the session, signed
 * out, session failed).
 */
import AppHeader from '@/components/AppHeader.vue'
import ErrorState from '@/components/ErrorState.vue'
import ToastStack from '@/components/ToastStack.vue'
import { useAuthStore } from '@/stores/auth.js'

const auth = useAuthStore()
</script>

<template>
  <a class="skip-link" href="#content">Skip to content</a>

  <AppHeader />

  <main id="content" class="shell">
    <div v-if="auth.status === 'loading' || auth.status === 'idle'" class="gate">
      <p class="gate__text">Opening your session…</p>
    </div>

    <div v-else-if="auth.status === 'anonymous'" class="gate">
      <h1 class="gate__title">PreviewApp</h1>
      <p class="gate__text">
        Self-service preview environments. Sign in with your France Médias Monde account to create
        and inspect previews.
      </p>

      <button
        v-if="auth.providerConfigured"
        type="button"
        class="btn btn--primary btn--lg"
        @click="auth.signIn()"
      >
        Sign in with Entra ID
      </button>

      <!-- The registration has an external lead time: an instance deployed without it
           should say so rather than offer a button that cannot work. -->
      <p v-else class="gate__misconfigured">
        Entra ID sign-in is not configured on this instance. Set
        <span class="mono">ENTRA_CLIENT_ID</span> and <span class="mono">ENTRA_API_SCOPE</span>,
        then redeploy.
      </p>
    </div>

    <div v-else-if="auth.status === 'error'" class="gate gate--error">
      <ErrorState
        :error="auth.error"
        title="Could not sign you in"
        retry-label="Try again"
        @retry="auth.initialize()"
      />
    </div>

    <RouterView v-else />
  </main>

  <ToastStack />
</template>

<style scoped>
.shell {
  max-width: var(--page-max);
  margin: 0 auto;
  padding: var(--sp-7) var(--sp-6) var(--sp-10);
}

.gate {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--sp-5);
  max-width: 52ch;
  margin: var(--sp-10) auto;
  text-align: center;
}

.gate--error {
  max-width: 640px;
  text-align: left;
}

.gate__title {
  font-size: var(--text-2xl);
  font-weight: var(--weight-semibold);
}

.gate__text {
  color: var(--c-text-muted);
  font-size: var(--text-md);
  line-height: var(--leading-loose);
}

.gate__misconfigured {
  padding: var(--sp-5) var(--sp-6);
  background: var(--st-updating-bg);
  border: 1px solid var(--st-updating-bd);
  border-radius: var(--radius-md);
  color: var(--st-updating-fg);
  font-size: var(--text-md);
  line-height: var(--leading-loose);
  text-align: left;
}

@media (max-width: 720px) {
  .shell {
    padding: var(--sp-6) var(--sp-5) var(--sp-9);
  }
}
</style>
